import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const callbackSchema = z.object({
  name: z.string().min(2, "Минимум 2 символа").max(100, "Максимум 100 символов"),
  phone: z.string().min(10, "Введите корректный номер телефона").max(20),
  comment: z.string().max(500, "Максимум 500 символов").optional(),
});

const CallbackForm = ({ onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    resolver: zodResolver(callbackSchema),
    defaultValues: {
      name: "",
      phone: "",
      comment: "",
    },
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/callback`, data);
      if (response.data.success) {
        toast.success("Заявка отправлена!", {
          description: response.data.message,
        });
        form.reset();
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      toast.error("Ошибка", {
        description: error.response?.data?.detail || "Не удалось отправить заявку",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" data-testid="callback-form">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-[#1A1A1A] font-medium">Ваше имя</FormLabel>
              <FormControl>
                <Input
                  placeholder="Иван"
                  className="input-custom"
                  data-testid="callback-name-input"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-[#1A1A1A] font-medium">Телефон</FormLabel>
              <FormControl>
                <Input
                  placeholder="+7 (999) 123-45-67"
                  className="input-custom"
                  data-testid="callback-phone-input"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="comment"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-[#1A1A1A] font-medium">Комментарий (необязательно)</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Удобное время для звонка..."
                  className="input-custom min-h-[80px] resize-none"
                  data-testid="callback-comment-input"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          className="btn-primary w-full"
          disabled={isSubmitting}
          data-testid="callback-submit-btn"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Отправка...
            </>
          ) : (
            "Перезвоните мне"
          )}
        </Button>
      </form>
    </Form>
  );
};

export default CallbackForm;
